import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Collapse,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';
import { CityValidationResult } from '../hooks/useCityValidation';

interface SerpCityValidatorProps {
  open: boolean;
  onClose: () => void;
  cities: string[];
  validationResults: Record<string, CityValidationResult> | null;
  isValidating: boolean;
  onUpdateCity: (oldCity: string, newCity: string) => void;
  onRemoveCity: (city: string) => void;
  onProceed: () => void;
}

export const SerpCityValidator: React.FC<SerpCityValidatorProps> = ({
  open,
  onClose,
  cities,
  validationResults,
  isValidating,
  onUpdateCity,
  onRemoveCity,
  onProceed,
}) => {
  const [expandedCities, setExpandedCities] = useState<Set<string>>(new Set());
  const [editingCity, setEditingCity] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Calculate summary
  const validCities = cities.filter(city => validationResults?.[city]?.valid);
  const invalidCities = cities.filter(city => validationResults?.[city] && !validationResults[city].valid);

  const toggleExpanded = (city: string) => {
    const newExpanded = new Set(expandedCities);
    if (newExpanded.has(city)) {
      newExpanded.delete(city);
    } else {
      newExpanded.add(city);
    }
    setExpandedCities(newExpanded);
  };

  const startEditing = (city: string) => {
    setEditingCity(city);
    setEditValue(city);
  };

  const saveEdit = () => {
    if (editingCity && editValue.trim() && editValue !== editingCity) {
      onUpdateCity(editingCity, editValue.trim());
    }
    setEditingCity(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCity(null);
    setEditValue('');
  };

  const selectSuggestion = (city: string, suggestion: any) => {
    // Use the canonical name from the suggestion
    const newCityName = suggestion.canonical_name || suggestion.name;
    onUpdateCity(city, newCityName);
    
    // Collapse the expanded section after selection
    const newExpanded = new Set(expandedCities);
    newExpanded.delete(city);
    setExpandedCities(newExpanded);
    
    // Mark this as resolved locally without re-validation
    // The parent component will handle the city update
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LocationCityIcon />
          <Typography variant="h6">City Validation Results</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {isValidating ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box mb={3}>
              <Alert 
                severity={invalidCities.length === 0 ? 'success' : invalidCities.length === cities.length ? 'error' : 'warning'}
              >
                <Typography variant="body2">
                  {validCities.length} of {cities.length} cities validated successfully
                  {invalidCities.length > 0 && ` (${invalidCities.length} need attention)`}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Cities with 90%+ confidence are automatically matched to their canonical names.
                </Typography>
              </Alert>
            </Box>

            {/* City List */}
            <List>
              {cities.map((city) => {
                const result = validationResults?.[city];
                const isExpanded = expandedCities.has(city);
                const isEditing = editingCity === city;

                return (
                  <React.Fragment key={city}>
                    <ListItem>
                      <ListItemIcon>
                        {result?.valid ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          isEditing ? (
                            <TextField
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              size="small"
                              autoFocus
                              fullWidth
                            />
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>{city}</Typography>
                              {result?.valid && result.confidence && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip 
                                    label={`${result.confidence}% match`} 
                                    size="small" 
                                    color={result.confidence >= 85 ? "success" : "warning"} 
                                    variant="outlined"
                                  />
                                  {result.confidence >= 85 && (
                                    <Chip 
                                      label="Auto-matched" 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          )
                        }
                        secondary={
                          result?.valid ? (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                Matched: {result.canonical_name}
                              </Typography>
                              {result.target_type && (
                                <Chip 
                                  label={result.target_type} 
                                  size="small" 
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="error">
                              {result?.error || 'Not found in SERP locations'}
                            </Typography>
                          )
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        {isEditing ? (
                          <>
                            <IconButton size="small" onClick={saveEdit}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEdit}>
                              <ErrorIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit city name">
                              <IconButton size="small" onClick={() => startEditing(city)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {result?.suggestions && result.suggestions.length > 0 && (
                              <IconButton size="small" onClick={() => toggleExpanded(city)}>
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                            <Tooltip title="Remove city">
                              <IconButton 
                                size="small" 
                                onClick={() => onRemoveCity(city)}
                                color="error"
                              >
                                <ErrorIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    {/* Suggestions */}
                    {result?.suggestions && result.suggestions.length > 0 && (
                      <Collapse in={isExpanded}>
                        <Box pl={7} pr={2} pb={2}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Did you mean:
                          </Typography>
                          <List dense>
                            {result.suggestions.map((suggestion, idx) => (
                              <ListItem 
                                key={idx}
                                button
                                onClick={() => selectSuggestion(city, suggestion)}
                                sx={{ 
                                  borderRadius: 1, 
                                  mb: 0.5,
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                              >
                                <ListItemText
                                  primary={suggestion.name}
                                  secondary={
                                    <>
                                      {suggestion.canonical_name}
                                      {suggestion.target_type && (
                                        <Chip 
                                          label={suggestion.target_type} 
                                          size="small" 
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                    </>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onProceed} 
          variant="contained" 
          disabled={isValidating || cities.length === 0}
          color={invalidCities.length > 0 ? 'warning' : 'primary'}
        >
          {invalidCities.length > 0 ? 'Proceed with Valid Cities' : 'Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 